import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode,
  useCallback
} from 'react'
import { t } from 'i18next'
import { useQueryClient } from 'react-query'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from '@reach/router'
import { AVISOS_LEXNET } from 'src/constants/routes'

import NoConnectionDialog from 'src/components/Layout/components/NoConnectionDialog'
import AlertDialog from 'src/components/shared/ui/AlertDialog/AlertDialog'
import { AlertType } from 'src/components/shared/ui/AlertDialog/enums'
import LoginDialog from 'src/components/shared/ui/LoginDialog/LoginDialog'
import {
  LoginStageStatus,
  LoginStageStep,
  LoginStageProp
} from 'src/components/shared/ui/LoginDialog/LoginStagesTypes'
import { LEXNET_COOKIE_TIMEOUT } from 'src/config'
import { useAlertContext } from 'src/contexts/AlertContext'
import useCookie from 'src/hooks/useCookie'
import { LexnetUser, Sustituto } from 'src/interfaces'
import { Colegio } from 'src/interfaces/Catalog'
import * as AuthenticationService from 'src/services/AuthenticationService'
import * as CryptoService from 'src/services/CryptoService'
import { logError } from 'src/services/EscritosServices'
import * as MessageService from 'src/services/MessageService'
import { setUserAction } from 'src/state/authentication/actions'
import { userSelector } from 'src/state/authentication/selectors'
import HttpError from 'src/utils/Http/HttpError'
import { getLexnetErrorMessage } from 'src/utils/lexnetErrors'
import { setLexnetActivityHandler } from 'src/utils/lexnetActivity'
import { hasPendingRequests } from 'src/utils/pendingRequests'
import { setLexnetSessionExpiredHandler } from 'src/utils/lexnetSessionExpired'

export const LexnetStatusContext = createContext({})
export const USERS_STORAGE_KEY = 'LexonUsers'

const RECHECK_WHILE_PENDING_MS = 30_000

export const LexnetStatusContextProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const navigate = useNavigate()
  const [users, setUsers] = useState([])
  const lexnetSessionTimer = useRef<NodeJS.Timeout | null>(null)
  const sessionExpiredHandled = useRef(false)
  const [connected, setConnected] = useState(false)
  const [lexnetUser, setLexnetUser] = useState<LexnetUser | null>(null)
  const [noConnectionOpen, setNoConnectionOpen] = useState(false)
  const [connectionSuccessOpen, setConnectionSuccessOpen] = useState(false)
  const [connectionExpiredOpen, setConnectionExpiredOpen] = useState(false)
  const [pendingSendEscrito, setPendingSendEscrito] = useState(false)
  const [needsSendEscrito, setNeedsSendEscrito] = useState(false)
  const [openLoginDialog, setOpenLoginDialog] = useState(false)
  const [newNotificationsOpen, setNewNotificationsOpen] = useState(false)
  const [selectedLoginState, setSelectedLoginState] = useState<LoginStageProp>({
    step: null,
    status: null
  })
  const { addAlert } = useAlertContext()
  const queryClient = useQueryClient()
  const {
    addLexnetCookie,
    existLexnetCookie,
    getLexnetCookieExpirationValue,
    getLexnetCookieUserText,
    removeLexnetCookie
  } = useCookie()
  const dispatch = useDispatch()
  const userState: any = useSelector(userSelector)

  let startTimerMS = 0
  let step = 0

  const createLexnetUser = (
    codigoColegiado: number,
    colegio: Colegio,
    dni = '',
    sustituto: Sustituto | null = null
  ) => {
    return {
      codigoColegiado,
      colegio,
      dni,
      sustituto
    } as LexnetUser
  }

  const getUsers = useCallback(() => {
    const usersSession = window.sessionStorage.getItem(USERS_STORAGE_KEY)
    return usersSession ? JSON.parse(usersSession) : []
    // eslint-disable-next-line
  }, [users])

  const getLexnetUserText = (user: LexnetUser | null): string => {
    if (user) {
      let userSel = user
      const { users } = getUsers()
      if (users !== undefined) {
        userSel = users.find(u => u.id === user.id)
      }
      if (userSel?.sustituto) {
        return `${t('form.field.colegiado')}: ${userSel.codigoColegiado} ${t(
          'form.legend.sustituto_autorizado'
        )} ${(userSel.sustituto as any).name} ${t('form.field.colegiado')} ${
          (userSel.sustituto as any).codigoColegiado
        } ${
          userSel.sustituto && (userSel.sustituto as any).colegio
            ? (userSel.sustituto as any).colegio.descripcion
            : ''
        }`
      }
      return `${t('form.field.colegiado')}: ${user?.codigoColegiado} (${
        user.colegio ? user.colegio.descripcion : ''
      })`
    }
    return ''
  }

  const lexnetUserText = () => {
    return getLexnetCookieUserText()
  }

  const lexnetDisconnect = () => {
    setConnected(false)
    removeLexnetCookie()
    if (lexnetSessionTimer.current !== null) {
      clearTimeout(lexnetSessionTimer.current)
    }
    lexnetSessionTimer.current = null
  }

  async function onDisconnect() {
    try {
      await AuthenticationService.endSession()
    } finally {
      lexnetDisconnect()
    }
  }

  function checkCookie() {
    if (hasPendingRequests()) {
      createTimer(RECHECK_WHILE_PENDING_MS)
      return
    }
    if (!existLexnetCookie()) {
      onDisconnect().catch(() => undefined)
      lexnetSessionTimer.current = null
      setConnectionExpiredOpen(true)
    }
  }

  function createTimer(time: number) {
    startTimerMS = new Date().getTime()
    const timeOut = setTimeout(checkCookie, time)
    lexnetSessionTimer.current = timeOut
  }

  function resetIdleTimer() {
    if (!connected || lexnetUser === null) {
      return
    }
    addLexnetCookie(getLexnetUserText(lexnetUser))
    if (lexnetSessionTimer.current !== null) {
      clearTimeout(lexnetSessionTimer.current)
    }
    createTimer(LEXNET_COOKIE_TIMEOUT * 60000 + 100)
  }

  const showMessage = (status: LoginStageStatus = LoginStageStatus.PROCESSING, error = '') => {
    setSelectedLoginState({
      step,
      status,
      error
    })
  }

  const showNextMessage = () => {
    showMessage(LoginStageStatus.SUCCESS)
    step += 1
    showMessage()
  }

  const showMessageError = (e: HttpError | TypeError) => {
    let message = t('general.error_lexnet_not_accessible')

    if (e instanceof HttpError) {
      message = getLexnetErrorMessage(e.response, t)
    } else if (typeof e === 'object' && (e as any).errorType) {
      const [, splitMessage] = (e as any).errorMessage.split(':')
      switch ((e as any).errorType) {
        case 'es.gob.afirma.core.AOCancelledOperationException':
          message = (e as any).errorMessage
          break
        case 'java.util.concurrent.TimeoutException':
          message = `${
            (e as any).errorMessage
          }. Revise la configuración de AutoFirma y intentelo de nuevo`
          break
        case 'java.lang.Exception':
          message = `${splitMessage}. Revise la configuración de AutoFirma y intentelo de nuevo`
          break
        default:
          break
      }
    }

    showMessage(LoginStageStatus.ERROR, message)
  }

  function resetLoginStates() {
    Object.keys(LoginStageStep).forEach(key => {
      // if (Number.isNaN(Number(LoginStageStep[key]))) {
      setSelectedLoginState({
        step: Number(key),
        status: LoginStageStatus.INITIAL,
        error: null
      })
      // }
    })
  }

  function storeUsers(users: []) {
    setUsers(users)
    window.sessionStorage.setItem(USERS_STORAGE_KEY, JSON.stringify({ users }))
  }

  function closeLoginDialog() {
    setOpenLoginDialog(false)
  }

  async function setLoggedUser(user: LexnetUser | null): Promise<boolean> {
    if (!user) {
      return false
    }

    /* eslint no-empty: ["error", { "allowEmptyCatch": true }] */
    try {
      await AuthenticationService.saveSelectedUser(user.id)
    } catch (e) {}

    /* eslint no-empty: ["error", { "allowEmptyCatch": true }] */
    try {
      const currentUser = await AuthenticationService.getUser()
      dispatch(setUserAction(currentUser))
    } catch (e) {}

    setLexnetUser(user)
    removeLexnetCookie()

    if (lexnetSessionTimer.current !== null) {
      clearTimeout(lexnetSessionTimer.current)
    }

    lexnetSessionTimer.current = null
    addLexnetCookie(getLexnetUserText(user))

    const time = LEXNET_COOKIE_TIMEOUT * 60000 + 100
    createTimer(time)

    queryClient.invalidateQueries(['avisosLexnetListed'])

    return true
  }

  async function onConnect(signal: boolean) {
    sessionExpiredHandled.current = false
    try {
      setOpenLoginDialog(true)
      showNextMessage() // start step 1
      // Llamada a AutoFirma
      const { token } = await AuthenticationService.getTokenToSign()

      if (!signal) return closeLoginDialog()

      showNextMessage() // end step 1 >= start step 2
      const signedToken = await CryptoService.signString(token)
      if (!signal) return closeLoginDialog()
      showNextMessage() // end step 2 >= start step 3
      // Llamada al API: /lexnet/login-token
      const result = await AuthenticationService.startSesion(signedToken)
      if (!signal) return closeLoginDialog()
      if (result.error) {
        try {
          AuthenticationService.saveDefaultUser(userState.id)
        } catch (e) {}
        if (result.error.code === -1) {
          setConnected(false)
          removeLexnetCookie()
          closeLoginDialog()
          addAlert({
            message: t('alert.not_valid_user_for_certificate'),
            type: AlertType.ERROR
          })
          return null
        }
      }

      storeUsers(result.usuarios)

      const loggedUser = result.usuarios.find(user => user.id === result.loggedUserId)

      const lexnetUser: LexnetUser = createLexnetUser(
        loggedUser.codigoColegiado,
        loggedUser.colegio,
        loggedUser.dni,
        loggedUser.sustituto
      )

      if (!signal) return closeLoginDialog()

      setLexnetUser(lexnetUser)
      dispatch(setUserAction(loggedUser))
      // Llamada al API: /acuses/aceptados-rechazados
      await MessageService.getAceptadosRechazados()
      showNextMessage() // end step 2 >= start step 3

      if (!signal) return closeLoginDialog()

      addLexnetCookie(getLexnetUserText(lexnetUser))
      setConnected(true)

      const time = LEXNET_COOKIE_TIMEOUT * 60000 + 100
      createTimer(time)

      closeLoginDialog()
      setConnectionSuccessOpen(true)
    } catch (e) {
      AuthenticationService.saveDefaultUser(userState.id)
      setConnected(false)
      removeLexnetCookie()
      showMessageError(e as HttpError)
      logError({
        origen: 'onConnect',
        error: e,
        traza: (e as any).stack,
        trazaDev: ''
      })
    } finally {
      if (pendingSendEscrito) {
        setNeedsSendEscrito(true)
        setPendingSendEscrito(false)
      }
    }
    return null
  }

  function onClose() {
    resetLoginStates()
    setNoConnectionOpen(false)
  }

  function showConnectDialog(pendingSendEscrito = false) {
    setPendingSendEscrito(pendingSendEscrito)
    setNoConnectionOpen(true)
  }

  function getSessionRemainingTime() {
    const time = LEXNET_COOKIE_TIMEOUT * 60000 + 100
    return time - (new Date().getTime() - startTimerMS)
  }

  useEffect(() => {
    setLexnetSessionExpiredHandler(() => {
      if (sessionExpiredHandled.current) {
        return
      }
      sessionExpiredHandled.current = true
      lexnetDisconnect()
      setConnectionExpiredOpen(true)
    })
    return () => setLexnetSessionExpiredHandler(null)
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    const isConnected = existLexnetCookie()
    setConnected(isConnected)
    if (isConnected) {
      const cookieValue: Date | undefined = getLexnetCookieExpirationValue()
      if (cookieValue !== undefined) {
        const current = new Date()
        const expire = new Date(cookieValue)
        const diff = Math.abs(expire.getTime() - current.getTime()) + 100
        createTimer(diff)
      }
    }
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    if (!connected) {
      return undefined
    }
    setLexnetActivityHandler(resetIdleTimer)
    return () => setLexnetActivityHandler(null)
    // eslint-disable-next-line
  }, [connected, lexnetUser])

  useEffect(() => {
    if (!openLoginDialog) {
      resetLoginStates()
    }
  }, [openLoginDialog])

  return (
    <LexnetStatusContext.Provider
      value={{
        connected,
        setConnected,
        lexnetUser,
        setLexnetUser,
        lexnetUserText,
        lexnetDisconnect,
        createLexnetUser,
        showConnectDialog,
        needsSendEscrito,
        setNeedsSendEscrito,
        setNewNotificationsOpen,
        setConnectionSuccessOpen,
        setNoConnectionOpen,
        noConnectionOpen,
        onConnect,
        onDisconnect,
        onClose,
        getSessionRemainingTime,
        users,
        getUsers,
        getLexnetUserText,
        setLoggedUser
      }}
    >
      {children}
      <NoConnectionDialog open={noConnectionOpen} onClose={onClose} onConnect={onConnect} />
      <LoginDialog
        show={openLoginDialog}
        selectedStage={selectedLoginState}
        close={() => closeLoginDialog()}
        disabledButton={false}
        buttonText={t('Cancelar')}
      />
      <AlertDialog
        show={connectionSuccessOpen}
        type={AlertType.SUCCESS}
        message={t('alert.successful_connection')}
        close={() => null}
        accept={() => null}
        cancel={() => {
          setConnectionSuccessOpen(false)
          queryClient.invalidateQueries(['avisosLexnetListed'])
        }}
      />
      <AlertDialog
        show={connectionExpiredOpen}
        type={AlertType.INFO}
        message={t('alert.timeout_session')}
        primaryButtonText={t('actions.accept')}
        secondaryButtonText={null}
        close={() => setConnectionExpiredOpen(false)}
        accept={() => {
          setConnectionExpiredOpen(false)
          showConnectDialog()
        }}
        cancel={() => setConnectionExpiredOpen(false)}
      />
      <AlertDialog
        show={newNotificationsOpen}
        type={AlertType.WARNING}
        message={t('alert.see_new_notifications')}
        close={() => setNewNotificationsOpen(false)}
        accept={() => setNewNotificationsOpen(false)}
        cancel={() => {
          navigate(AVISOS_LEXNET())
          setNewNotificationsOpen(false)
        }}
        primaryButtonText={t('actions.seeNotifications')}
        secondaryButtonText={t('actions.close')}
      />
    </LexnetStatusContext.Provider>
  )
}

export function useLexnetStatusContext(): any {
  return useContext(LexnetStatusContext)
}
